import { MuscleTag } from '@/generated';
import {
  bodyZoneApi,
  deckApi,
  equipmentApi,
  exerciseApi,
  exerciseCategoryApi,
  exerciseFactsApi,
  glossaryApi,
  muscleTagApi,
  planApi,
  traineeApi,
  trainingHistoryApi,
  userApi,
} from '@/generated/clients';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export const queryKeys = {
  BodyZones: 'body-zone',
  Exercise: 'exercise',
  ExerciseCategories: 'exercise-categories',
  ExerciseFacts: 'exercise-facts',
  Deck: 'deck',
  Decks: 'decks',
  Equipment: 'equipment',
  GlossaryTerms: 'glossary-terms',
  TrainingHistory: 'training-history',
  TrainingHistoryByUser: 'training-history-by-user',
  MuscleTags: 'muscle-tags',
  Plan: 'plan',
  Plans: 'plans',
  Trainees: 'trainees',
  TraineeProfile: 'trainee-profile',
  AllDecks: 'all-decks',
  AllExercises: 'all-exercises',
  DeckExercises: 'deck-exercises',
  DeckValidation: 'deck-validation',
};

export const useValidateDeckQuery = (deckId?: string) => {
  const query = useQuery({
    queryKey: [queryKeys.DeckValidation, deckId],
    enabled: !!deckId,
    queryFn: ({ queryKey }) => {
      const [, deckId] = queryKey;
      return deckApi.deckValidate({ deckId: deckId! });
    },
  });
  return { validationResult: query.data, ...query };
};

export const useDeckQuery = (deckId?: string) => {
  const query = useQuery({
    enabled: !!deckId,
    queryKey: [queryKeys.Deck, deckId],
    queryFn: ({ queryKey }) => {
      const [, deckId] = queryKey;
      return deckApi.deckGetDeckById({ deckId: deckId! });
    },
  });

  return { deck: query.data, ...query };
};

export const useListUserDecksQuery = (enabled: boolean) => {
  const query = useQuery({
    queryKey: [queryKeys.Decks],
    queryFn: () => {
      return deckApi.deckGetUserDecks({});
    },
    enabled,
  });

  return { decks: query.data, ...query };
};

export const useListSpecificUserDecks = (userId?: string) => {
  const query = useQuery({
    queryKey: [queryKeys.Decks, userId],
    queryFn: ({ queryKey }) => {
      const [, userId] = queryKey;
      if (userId) return deckApi.deckGetDecksByUserId({ getUserDecksDTO: { userId: userId, listOnlyEditableDecks: false } });
    },
    enabled: userId !== undefined,
  });
  return { decks: query.data, ...query };
};

export const useListAllDecksQuery = (enabled: boolean) => {
  const query = useQuery({
    queryKey: [queryKeys.AllDecks],
    queryFn: () => {
      return deckApi.deckListAllDecks({});
    },
    enabled,
  });

  return { decks: query.data, ...query };
};

export const useDeckExercisesQuery = (deckId?: string) => {
  const query = useQuery({
    enabled: !!deckId,
    queryKey: [queryKeys.DeckExercises, deckId],
    queryFn: ({ queryKey }) => {
      const [, deckId] = queryKey;
      return exerciseApi.exerciseListByDeckId({ deckId: deckId! });
    },
  });

  return { exercises: query.data, ...query };
};

export const useDeckExercisesQueries = (deckIds: string[] | undefined) => {
  const uniqueDeckIds = [...new Set(deckIds?.filter((id): id is string => !!id))];

  const queries = useQueries({
    queries: uniqueDeckIds.map((deckId) => ({
      queryKey: [queryKeys.DeckExercises, deckId],
      queryFn: () =>
        exerciseApi.exerciseListByDeckId({
          deckId,
        }),
    })),
  });

  const exercisesByDeckId = Object.fromEntries(uniqueDeckIds.map((deckId, index) => [deckId, queries[index].data]));

  return {
    exercisesByDeckId,
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
  };
};

export const useAllExercisesQuery = (enabled: boolean) => {
  const query = useQuery({
    queryKey: [queryKeys.AllExercises],
    enabled,
    queryFn: () => {
      return exerciseApi.exerciseList();
    },
  });

  return { exercises: query.data, ...query };
};

export const useExerciseQuery = (exerciseId?: string) => {
  const query = useQuery({
    enabled: !!exerciseId,
    queryKey: [queryKeys.Exercise, exerciseId],
    queryFn: ({ queryKey }) => {
      const [, exerciseId] = queryKey;
      return exerciseApi.exerciseExerciseById({ exerciseId: exerciseId! });
    },
  });

  return { exercise: query.data, ...query };
};

export const useExerciseCategoriesQuery = (enabled: boolean) => {
  const query = useQuery({
    queryKey: [queryKeys.ExerciseCategories],
    enabled,
    queryFn: () => {
      return exerciseCategoryApi.exerciseCategoryList();
    },
  });

  return { exerciseCategories: query.data, ...query };
};

export const usePlansQuery = () => {
  const query = useQuery({
    queryKey: [queryKeys.Plans],
    queryFn: () => {
      return planApi.planList();
    },
  });

  return { planList: query.data, ...query };
};

export const usePlanQuery = (planId?: string) => {
  const query = useQuery({
    enabled: !!planId,
    queryKey: [queryKeys.Plan, planId],
    queryFn: () => {
      return planApi.planById({ planId: planId! });
    },
  });

  return { plan: query.data, ...query };
};

export const useTraineesQuery = (enabled: boolean) => {
  const query = useQuery({
    queryKey: [queryKeys.Trainees],
    enabled,
    queryFn: () => {
      return traineeApi.traineeManagementListTrainees();
    },
  });

  return { trainees: query.data, ...query };
};

export const useGlossaryQuery = () => {
  const query = useQuery({
    queryKey: [queryKeys.GlossaryTerms],
    queryFn: () => {
      return glossaryApi.glossaryList();
    },
  });

  return { glossaryTerms: query.data, ...query };
};

export const useExerciseFactsQuery = () => {
  const query = useQuery({
    queryKey: [queryKeys.ExerciseFacts],
    queryFn: () => {
      return exerciseFactsApi.exerciseFactList();
    },
  });
  return { exerciseFacts: query.data, ...query };
};

export const useEquipmentListQuery = () => {
  const query = useQuery({
    queryKey: [queryKeys.Equipment],
    queryFn: () => {
      return equipmentApi.equipmentList();
    },
  });
  return { equipmentList: query.data, ...query };
};

export const useMuscleTagsQuery = () => {
  const query = useQuery({
    queryKey: [queryKeys.MuscleTags],
    queryFn: () => muscleTagApi.muscleTagListAll(),
  });

  const muscleTagMap = useMemo(() => {
    const map = new Map<string, MuscleTag>();

    query.data?.forEach((tag) => {
      map.set(tag.id, tag);
    });

    return map;
  }, [query.data]);

  return {
    ...query,
    muscleTags: query.data,
    muscleTagMap,
  };
};

export const useBodyZoneListQuery = () => {
  const query = useQuery({
    queryKey: [queryKeys.BodyZones],
    queryFn: async () => {
      return await bodyZoneApi.bodyZoneList();
    },
  });
  return { bodyZoneList: query.data, ...query };
};

export const useTrainingHistoryQuery = (enabled: boolean) => {
  const query = useQuery({
    enabled: enabled,
    queryKey: [queryKeys.TrainingHistory],
    queryFn: async () => {
      return await trainingHistoryApi.trainingHistoryGetHistory();
    },
  });
  return { trainingHistory: query.data, ...query };
};

export const useTrainingHistoryByUserQuery = (userId?: string) => {
  const query = useQuery({
    enabled: !!userId,
    queryKey: [queryKeys.TrainingHistoryByUser, userId],
    queryFn: async ({ queryKey }) => {
      const [, userId] = queryKey;
      return await trainingHistoryApi.trainingHistoryGetHistoryByUser({ userId: userId! });
    },
  });
  return { trainingHistoryByUser: query.data, ...query };
};

export const useTraineeProfileQuery = (id?: string) => {
  const query = useQuery({
    enabled: id !== undefined,
    queryKey: [queryKeys.TraineeProfile],
    queryFn: async () => {
      return await userApi.userGetUserProfileForTrainer({ id: id! });
    },
  });
  return { profile: query.data, ...query };
};
